import type { AuditEvent } from "../access/api";

/**
 * The account journal ([NF-017], [AC-067]) for reading: what each recorded
 * action means, and what changed. The journal stores ids, roles and states -
 * never a name or an address - so the page resolves an account id to the
 * display name the administration sees, and says "ehemaliges Konto" for an id
 * it no longer knows.
 */

/** i18n keys of the actions the module records. An unknown action shows its code. */
export const ACTION_KEY: Record<string, string> = {
  login: "adminJournal.actions.login",
  "login.refused": "adminJournal.actions.loginRefused",
  logout: "adminJournal.actions.logout",
  "system.login": "adminJournal.actions.systemLogin",
  "system.login.refused": "adminJournal.actions.systemLoginRefused",
  "system.login-code.refused": "adminJournal.actions.systemLoginCodeRefused",
  "account.first-login": "adminJournal.actions.firstLogin",
  "account.reset": "adminJournal.actions.reset",
  "account.display-name": "adminJournal.actions.displayName",
  "account.password": "adminJournal.actions.password",
  "account.email": "adminJournal.actions.email",
  "account.create": "adminJournal.actions.create",
  "account.update": "adminJournal.actions.update",
  "account.invite": "adminJournal.actions.invite",
  "account.recover": "adminJournal.actions.recover",
  "account.anonymize": "adminJournal.actions.anonymize",
  "session.revoke": "adminJournal.actions.sessionRevoke",
  "session.elevate": "adminJournal.actions.elevate",
  "session.elevate.refused": "adminJournal.actions.elevateRefused",
};

/** A refused action - shown so the eye finds it. */
export function isRefusal(action: string): boolean {
  return action.endsWith(".refused");
}

export type ChangeField = "roles" | "state" | "is_admin";

export interface FieldChange {
  field: ChangeField;
  from: unknown;
  to: unknown;
}

/**
 * What an `account.update` changed: old AND new value per field (module
 * 0.3.0) - "Rollen jetzt X" alone says nothing about what was taken away.
 */
export function fieldChanges(event: AuditEvent): FieldChange[] {
  const from = asRecord(event.details.from);
  const to = asRecord(event.details.to);
  const fields: ChangeField[] = ["roles", "state", "is_admin"];
  return fields
    .filter((field) => field in to || field in from)
    .map((field) => ({ field, from: from[field], to: to[field] }));
}

/** The roles an `account.create` gave. */
export function createdRoles(event: AuditEvent): string[] {
  const roles = event.details.roles;
  return Array.isArray(roles) ? roles.filter((role): role is string => typeof role === "string") : [];
}

/** Whether an invitation's mail left (`account.invite`); null when not recorded. */
export function inviteSent(event: AuditEvent): boolean | null {
  return typeof event.details.sent === "boolean" ? event.details.sent : null;
}

/** Details the page does not summarise, compact - ids, roles and states only, by construction of the journal. */
export function otherDetails(event: AuditEvent): string | null {
  const known = new Set(["from", "to", "roles", "sent", "administration", "display_name_changed"]);
  const rest = Object.fromEntries(Object.entries(event.details).filter(([key]) => !known.has(key)));
  return Object.keys(rest).length > 0 ? JSON.stringify(rest) : null;
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" ? (value as Record<string, unknown>) : {};
}
